import axios from "axios";
import { baseURL } from "./index";

// Pages a signed-out visitor is supposed to be on. Redirecting to /login from one
// of these would reload the page the user is already using (and, on
// /forgot-password/:token, would throw away the reset link), so we only clear the
// stored session there instead of navigating.
const AUTH_PAGE_PATHS = ["/login", "/register", "/forgot-password"];

const isOnAuthPage = () => {
  const path = window.location.pathname;
  return AUTH_PAGE_PATHS.some((authPath) => path === authPath || path.startsWith(authPath + "/"));
};

// Only session tokens ('access') may be stored as the user's login. The backend also
// issues short lived pwd_reset and email_verify tokens signed with the same key; those
// must never end up in localStorage/sessionStorage as a session.
const isAccessToken = (jwt) => {
  try {
    const segment = jwt.split(".")[1];
    if (!segment) return false;
    // base64url -> base64, then restore the padding atob needs
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const decoded = JSON.parse(
      decodeURIComponent(
        atob(padded)
          .split("")
          .map((char) => "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
    return decoded?.typ === "access";
  } catch (error) {
    return false;
  }
};

const axiosInterceptors = () => {
  // Helper function to clear auth data and redirect
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    localStorage.removeItem("expiresIn");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("expiresIn");
    sessionStorage.removeItem("userData");
    // Guard against a redirect loop: /login itself calls the API, and a 401 there
    // would otherwise reload the page forever.
    if (!isOnAuthPage()) {
      window.location.href = window.location.origin + "/login";
    }
  };

  // Use token from either localStorage or sessionStorage
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  
  // Initialize headers object
  let headers = {};
  
  // Check token expiration only if there's a token
  // Check both storages - sessionStorage may be empty on cold start (e.g. after closing tab with Remember Me)
  if (token) {
    const tokenExpireTime = sessionStorage.getItem("expiresIn") || localStorage.getItem("expiresIn");
    const currentTime = new Date().getTime();

    if (tokenExpireTime) {
      try {
        // Convert to number for comparison
        const expiryTime = parseInt(tokenExpireTime);
        if (currentTime > expiryTime || expiryTime === 0) {
          console.log("Authentication session expired");
          handleLogout();
          return axios.create({ baseURL, withCredentials: true }); // Return a basic axios instance without token
        }
      } catch (error) {
        // If expiry time is invalid, clear and redirect
        console.log("Invalid session data, re-authentication required");
        handleLogout();
        return axios.create({ baseURL, withCredentials: true }); // Return a basic axios instance without token
      }
    }
    
    // IMPORTANT: The backend's AuthGuard middleware expects the Authorization header
    // to have the format 'Bearer [token]', so we need to include the 'Bearer ' prefix
    headers.Authorization = `Bearer ${token}`;
  }

  const axiosInstance = axios.create({
    baseURL: baseURL,
    headers,
    // Accept the shared-session cookie the API sets on login (and drop it on
    // logout). The bearer header above still authenticates every request; the
    // cookie is only read by the API when no header is present.
    withCredentials: true,
    validateStatus: function (status) {
      return status <= 500;
    },
  });

  // Add request interceptor to ensure token is always fresh
  axiosInstance.interceptors.request.use(
    (config) => {
      // Always check for fresh token before each request
      const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      // Check for Welcome to the API message, which indicates auth failure
      if (response?.data?.message === 'Welcome to the API') {
        console.error("Authentication failed - got Welcome message instead of user data");
        // Don't automatically log out - just return error
        return Promise.reject(new Error('Authentication failed'));
      }
      
      if (response?.data?.status === 401) {
        console.log("401 Unauthorized response received");
        handleLogout();
        return Promise.reject(new Error('Unauthorized'));
      }

      // validateStatus resolves any status <= 500, so a real expired-token
      // 401/403 lands here as a "success" response instead of an error.
      // Detect the HTTP status directly so it still logs the user out.
      // A 403 only means the session is unusable when it came from AuthGuard /
      // AdminGuard, which always answer with `status` in the body. Business rule
      // 403s (e.g. a bid over the user's bid power) use `code`/`success` instead and
      // must be handed to the caller, not treated as a logout.
      if (response?.status === 401 || (response?.status === 403 && response?.data?.status === 403)) {
        console.log(`${response.status} Unauthorized response received`);
        handleLogout();
        return Promise.reject(new Error('Unauthorized'));
      }

      if (response?.data?.data?.token && isAccessToken(response.data.data.token)) {
        console.log("Received new token in response");
        // Update token and expiry in both storage locations
        const token = response.data.data.token;
        localStorage.setItem("token", token);
        sessionStorage.setItem("token", token);
        sessionStorage.setItem(
          "expiresIn",
          (new Date().getTime() + 3600000 * 48).toString() // Store as string
        );
      }

      return Promise.resolve(response.data);
    },
    (error) => {
      // Handle network errors or other axios errors
      console.log("Axios error:", error.message);
      if (error.response?.status === 401) {
        handleLogout();
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export default axiosInterceptors;